"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { API_ENDPOINTS, getApiErrorDetail, useApi } from "@/lib/api"
import { MigrationDetail, MigrationRow, MigrationStatus, PROVIDERS } from "@/types/payroll"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { useToast } from "@/hooks/use-toast"
import { useTranslation } from "@/hooks/use-translation"
import { invalidateHubCounts } from "@/lib/hub-counts-refresh"
import { formatDateTime, formatNumber } from "@/lib/locale"
import type { TranslationKey } from "@/translations"
import { AlertCircle, CheckCircle2, Clock3, Loader2, RefreshCw, Users } from "lucide-react"
import { DEFAULT_PAGE_SIZE, TablePager, pageSlice } from "@/components/table-pager"
import {
  ActiveFilters,
  ChoiceChips,
  FilterSection,
  FiltersButton,
  RowCard,
  RowList,
  RowMeta,
  RowName,
  SearchBox,
  SortChip,
  StatusChip,
  SummaryCard,
  type ActiveFilterChip,
  type SortOption,
} from "@/components/row-card"
import { MigrationDetailDialog } from "./migration-detail-dialog"

const STATUSES: MigrationStatus[] = ["Pending", "WaitingForWorkOrder", "Sent", "Created", "Failed", "Skipped", "Ignored", "Resolved"]
const ALL = "__all__"

/** The server list window; also the number quoted in the truncation notice. */
const LIST_TAKE = 500

/**
 * Everyday wording for each internal status — used everywhere the status is
 * shown, with the migration-record status name kept in parentheses so screens,
 * API responses, and logs can be cross-referenced. The wording itself lives in
 * translations/index.ts (the raw status code inside the parentheses is NOT
 * translated); this map is the status → translation key lookup.
 */
export const STATUS_LABEL_KEYS: Record<MigrationStatus, TranslationKey> = {
  Pending: "workers.status.pending",
  WaitingForWorkOrder: "workers.status.waitingForWorkOrder",
  Sent: "workers.status.sent",
  Created: "workers.status.created",
  Failed: "workers.status.failed",
  Skipped: "workers.status.skipped",
  Ignored: "workers.status.ignored",
  Resolved: "workers.status.resolved",
}

/** Status tone mapping: done → success, failing → error, moving → info, inert → neutral. */
export const STATUS_VARIANT: Record<MigrationStatus, "success" | "error" | "info" | "neutral" | "warning"> = {
  Pending: "info",
  WaitingForWorkOrder: "warning",
  Sent: "info",
  Created: "success",
  Failed: "error",
  Skipped: "neutral",
  Ignored: "neutral",
  Resolved: "neutral",
}

type SortKey = "workerName" | "provider" | "company" | "status" | "providerEmployeeId" | "attemptCount" | "createdOn"

const SORT_OPTION_KEYS: { key: SortKey; labelKey: TranslationKey }[] = [
  { key: "createdOn", labelKey: "common.added" },
  { key: "workerName", labelKey: "workers.sort.worker" },
  { key: "status", labelKey: "workers.filter.progress" },
  { key: "provider", labelKey: "common.payrollSystem" },
  { key: "company", labelKey: "workers.sort.organization" },
  { key: "providerEmployeeId", labelKey: "workers.meta.employeeId" },
  { key: "attemptCount", labelKey: "workers.meta.tries" },
]

/**
 * Comparable value per sortable column. `statusLabel` is passed in because the
 * progress column sorts on the wording the operator can see, which is
 * language-dependent.
 */
function sortValue(
  row: MigrationRow,
  key: SortKey,
  fallbackName: (row: MigrationRow) => string,
  statusLabel: (status: MigrationStatus) => string
): string | number {
  switch (key) {
    case "workerName":
      return (row.workerName || fallbackName(row)).toLowerCase()
    case "provider":
      return row.provider.toLowerCase()
    case "company":
      return (row.internalOrganizationCode ?? "").toLowerCase()
    case "status":
      return statusLabel(row.status)
    case "providerEmployeeId":
      return (row.providerEmployeeId ?? "").toLowerCase()
    case "attemptCount":
      return row.attemptCount
    case "createdOn":
      return new Date(row.createdOn).getTime()
  }
}

export function MigrationsPage() {
  const { payrollApi } = useApi()
  const { toast } = useToast()
  const { t, language } = useTranslation()

  /** The everyday wording for a status; an unknown status shows its raw code. */
  const statusLabel = useCallback(
    (status: MigrationStatus) => {
      const key = STATUS_LABEL_KEYS[status]
      return key ? t(key) : String(status)
    },
    [t]
  )

  /** Display name for a worker whose name the API did not return. */
  const profileName = useCallback((row: { userProfileId: number }) => t("workers.profileNumber", { id: row.userProfileId }), [t])

  const [rows, setRows] = useState<MigrationRow[]>([])
  const [hasMore, setHasMore] = useState(false)
  /** Per-status totals from the server aggregate (same filters, minus progress); null = in flight or failed, shown as "—" rather than a false zero. */
  const [serverCounts, setServerCounts] = useState<Record<string, number> | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [provider, setProvider] = useState(ALL)
  const [status, setStatus] = useState(ALL)
  const [workerSearch, setWorkerSearch] = useState("")
  // Read via ref inside load() so Enter/Refresh always search the current
  // text without making every keystroke a request (stale-closure-safe).
  const workerSearchRef = useRef("")

  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")
  const [sortKey, setSortKey] = useState<SortKey>("createdOn")
  const [sortAsc, setSortAsc] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const [detail, setDetail] = useState<MigrationDetail | null>(null)
  const [filtersOpen, setFiltersOpen] = useState(false)

  /** The filters shared by the list and the counts aggregate (everything but progress). */
  const buildSharedParams = useCallback(() => {
    const params = new URLSearchParams()
    if (provider !== ALL) params.set("provider", provider)
    const workerTerm = workerSearchRef.current.trim()
    if (workerTerm) params.set("worker", workerTerm)
    if (dateFrom) params.set("from", new Date(`${dateFrom}T00:00:00`).toISOString())
    if (dateTo) params.set("to", new Date(`${dateTo}T23:59:59.999`).toISOString())
    return params
  }, [provider, dateFrom, dateTo])

  // Monotonic request tokens: rapid filter changes (e.g. typing in a date
  // field) fire overlapping requests, and only the latest one may land —
  // otherwise the cards and the list can describe different filters.
  const loadSeq = useRef(0)
  const countsSeq = useRef(0)

  const load = useCallback(async () => {
    const seq = ++loadSeq.current
    setIsLoading(true)
    setError(null)
    try {
      // Everything filters SERVER-side (before the fetch window), so the list
      // sees the whole history; take is the server maximum and hasMore reports
      // whether the window was truncated — never presented as the complete list.
      const params = buildSharedParams()
      if (status !== ALL) params.set("status", status)
      params.set("take", String(LIST_TAKE))
      const response = await payrollApi.get<{ data: MigrationRow[]; hasMore?: boolean }>(
        `${API_ENDPOINTS.PAYROLL.MIGRATIONS}?${params.toString()}`
      )
      if (seq !== loadSeq.current) return
      setRows(response.data ?? [])
      setHasMore(response.hasMore === true)
      setPage(1)
    } catch (err) {
      if (seq !== loadSeq.current) return
      // Server error text is shown as-is; only the fallback is translated.
      setError(getApiErrorDetail(err) ?? t("workers.error.load"))
    } finally {
      if (seq === loadSeq.current) {
        setIsLoading(false)
      }
    }
    // workerSearch is deliberately not a dependency: searching fires on Enter
    // or Refresh, not per keystroke (review finding — request-per-keypress
    // with a stale-response race).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildSharedParams, status])

  /**
   * The summary cards use a database aggregate under the same filters (minus
   * progress), so their numbers stay truthful beyond the 500-row list window.
   * Deliberately independent of `status`: a progress click cannot change the
   * aggregate, so it must not re-run it. While a request is in flight — and
   * on failure — the counts are null and the cards show "—": never a zero or
   * a stale number, which are exactly what the aggregate exists to avoid.
   */
  const loadCounts = useCallback(async () => {
    const seq = ++countsSeq.current
    setServerCounts(null)
    try {
      const response = await payrollApi.get<{ counts: Record<string, number> }>(
        `${API_ENDPOINTS.PAYROLL.MIGRATION_STATUS_COUNTS}?${buildSharedParams().toString()}`
      )
      if (seq === countsSeq.current) {
        setServerCounts(response.counts ?? {})
      }
    } catch {
      if (seq === countsSeq.current) {
        setServerCounts(null)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildSharedParams])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    void loadCounts()
  }, [loadCounts])

  /** Per-status totals for the summary cards; null when the aggregate failed. */
  const statusCounts = useMemo(() => {
    if (serverCounts === null) return null
    const counts = {} as Record<MigrationStatus, number>
    for (const s of STATUSES) counts[s] = serverCounts[s] ?? 0
    return counts
  }, [serverCounts])

  const totalCount = useMemo(
    () => (statusCounts === null ? null : Object.values(statusCounts).reduce((sum, n) => sum + n, 0)),
    [statusCounts]
  )

  // Every filter runs server-side; sort and paging happen over the loaded window.
  const filteredSorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      const av = sortValue(a, sortKey, profileName, statusLabel)
      const bv = sortValue(b, sortKey, profileName, statusLabel)
      const cmp = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv))
      return sortAsc ? cmp : -cmp
    })
  }, [rows, sortKey, sortAsc, profileName, statusLabel])

  const visible = pageSlice(filteredSorted, page, pageSize)

  const sortOptions: SortOption<SortKey>[] = SORT_OPTION_KEYS.map((o) => ({ key: o.key, label: t(o.labelKey) }))

  const changeSortKey = (key: SortKey) => {
    setSortKey(key)
    setSortAsc(key !== "createdOn")
    setPage(1)
  }

  const setStatusFilter = (value: string) => {
    setStatus(value)
    setPage(1)
  }

  /** Summary card click: apply that progress filter, or clear it when already active. */
  const toggleStatusCard = (value: string) => setStatusFilter(status === value ? ALL : value)

  const clearFilters = () => {
    setProvider(ALL)
    setStatusFilter(ALL)
    setDateFrom("")
    setDateTo("")
  }

  const activeChips: ActiveFilterChip[] = [
    // Provider names are the provider's own, never translated.
    ...(provider !== ALL ? [{ key: "provider", label: provider, onRemove: () => setProvider(ALL) }] : []),
    ...(status !== ALL
      ? [{ key: "status", label: statusLabel(status as MigrationStatus), onRemove: () => setStatusFilter(ALL) }]
      : []),
    ...(dateFrom ? [{ key: "from", label: t("workers.chip.addedFrom", { date: dateFrom }), onRemove: () => setDateFrom("") }] : []),
    ...(dateTo ? [{ key: "to", label: t("workers.chip.addedTo", { date: dateTo }), onRemove: () => setDateTo("") }] : []),
  ]

  const openDetail = async (row: MigrationRow) => {
    try {
      const response = await payrollApi.get<MigrationDetail>(
        API_ENDPOINTS.PAYROLL.MIGRATION_DETAIL(row.provider, row.userProfileId, row.internalOrganizationId)
      )
      setDetail(response)
    } catch (err) {
      toast({
        variant: "destructive",
        title: t("workers.toast.detailLoadFailed"),
        description: getApiErrorDetail(err) ?? String(err),
      })
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t("workers.intro")}</p>

      {/* Summary count cards — click one to filter by that progress. */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          icon={<Users />}
          label={t("workers.card.all")}
          count={totalCount}
          active={status === ALL}
          onClick={() => setStatusFilter(ALL)}
        />
        <SummaryCard
          icon={<AlertCircle />}
          label={t("workers.card.needsAttention")}
          count={statusCounts?.Failed ?? null}
          active={status === "Failed"}
          onClick={() => toggleStatusCard("Failed")}
        />
        <SummaryCard
          icon={<Clock3 />}
          label={t("workers.status.waitingForWorkOrder")}
          count={statusCounts?.WaitingForWorkOrder ?? null}
          active={status === "WaitingForWorkOrder"}
          onClick={() => toggleStatusCard("WaitingForWorkOrder")}
        />
        <SummaryCard
          icon={<CheckCircle2 />}
          label={t("workers.card.addedToPayroll")}
          count={statusCounts?.Created ?? null}
          active={status === "Created"}
          onClick={() => toggleStatusCard("Created")}
        />
      </div>

      {/* Toolbar: search + Filters chip left, sort + refresh right. */}
      <div className="flex flex-wrap items-center gap-2">
        <SearchBox
          placeholder={t("workers.searchPlaceholder")}
          aria-label={t("workers.searchPlaceholder")}
          value={workerSearch}
          onChange={(e) => {
            setWorkerSearch(e.target.value)
            workerSearchRef.current = e.target.value
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              void load()
              void loadCounts()
            }
          }}
        />
        <FiltersButton activeCount={activeChips.length} expanded={filtersOpen} onClick={() => setFiltersOpen(true)} />
        <div className="ml-auto flex items-center gap-2">
          <SortChip
            options={sortOptions}
            sortKey={sortKey}
            asc={sortAsc}
            onKeyChange={changeSortKey}
            onDirectionToggle={() => setSortAsc((v) => !v)}
          />
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            aria-label={t("common.refresh")}
            onClick={() => {
              void load()
              void loadCounts()
              invalidateHubCounts()
            }}
            disabled={isLoading}
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <ActiveFilters chips={activeChips} onClearAll={clearFilters} />

      {hasMore && !isLoading && !error && (
        <div className="rounded-[8px] bg-[var(--th-canvas)] px-4 py-2.5 text-sm text-muted-foreground">
          {t("workers.limitNotice", { count: formatNumber(LIST_TAKE, language) })}
        </div>
      )}

      {/* Filter drawer — every filter lives here, as Timesheet Hub does. */}
      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <SheetContent side="right">
          <SheetHeader>
            <SheetTitle className="text-xl">{t("common.filters")}</SheetTitle>
          </SheetHeader>
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-4">
            <FilterSection label={t("common.payrollSystem")}>
              <ChoiceChips
                ariaLabel={t("workers.filter.byPayrollSystemAria")}
                value={provider}
                onChange={(v) => {
                  setProvider(v)
                  setPage(1)
                }}
                options={[{ value: ALL, label: t("common.all") }, ...PROVIDERS.map((p) => ({ value: p, label: p }))]}
              />
            </FilterSection>
            <FilterSection label={t("workers.filter.progress")}>
              <ChoiceChips
                ariaLabel={t("workers.filter.byProgressAria")}
                value={status}
                onChange={setStatusFilter}
                options={[
                  { value: ALL, label: t("common.any"), count: totalCount ?? undefined },
                  ...STATUSES.map((s) => ({ value: s, label: statusLabel(s), count: statusCounts?.[s] })),
                ]}
              />
            </FilterSection>
            <FilterSection label={t("workers.filter.addedBetween")}>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="mig-date-from" className="text-xs text-muted-foreground">
                    {t("common.from")}
                  </Label>
                  <Input
                    id="mig-date-from"
                    type="date"
                    value={dateFrom}
                    onChange={(e) => {
                      setDateFrom(e.target.value)
                      setPage(1)
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="mig-date-to" className="text-xs text-muted-foreground">
                    {t("common.upTo")}
                  </Label>
                  <Input
                    id="mig-date-to"
                    type="date"
                    value={dateTo}
                    onChange={(e) => {
                      setDateTo(e.target.value)
                      setPage(1)
                    }}
                  />
                </div>
              </div>
            </FilterSection>
          </div>
          <SheetFooter>
            <Button variant="ghost" className="rounded-full" onClick={clearFilters}>
              {t("common.clearFilters")}
            </Button>
            <Button className="ml-auto rounded-full" onClick={() => setFiltersOpen(false)}>
              {t("common.done")}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {error ? (
        <Card>
          <CardContent className="space-y-3 py-8 text-center text-sm">
            <p className="text-destructive">{error}</p>
            <Button variant="outline" size="sm" onClick={() => void load()}>
              {t("common.tryAgain")}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground" aria-live="polite">
            {isLoading
              ? t("common.loading")
              : t("workers.countOf", {
                  shown: formatNumber(visible.length, language),
                  total: formatNumber(filteredSorted.length, language),
                })}
          </p>
          <RowList>
            {isLoading ? (
              <div className="flex h-24 items-center justify-center rounded-[14px] bg-white">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : visible.length === 0 ? (
              <div className="flex h-24 items-center justify-center rounded-[14px] bg-white text-sm text-muted-foreground">
                {t("workers.empty")}
              </div>
            ) : (
              visible.map((row) => {
                const tone = STATUS_VARIANT[row.status] ?? "neutral"
                return (
                  <RowCard key={`${row.provider}-${row.userProfileId}-${row.internalOrganizationId}`} accent={tone}>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="min-w-[220px] flex-1">
                        <div className="text-xs text-muted-foreground">
                          {t("workers.addedOn", { date: formatDateTime(row.createdOn, language) })}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-2">
                          <RowName>{row.workerName || profileName(row)}</RowName>
                          <Badge variant="warning">{row.provider}</Badge>
                          {row.internalOrganizationCode ? (
                            <Badge variant="neutral">{row.internalOrganizationCode}</Badge>
                          ) : row.internalOrganizationId > 0 ? (
                            <Badge variant="neutral">#{row.internalOrganizationId}</Badge>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusChip tone={tone}>{statusLabel(row.status)}</StatusChip>
                        <Button size="sm" onClick={() => void openDetail(row)}>
                          {t("common.open")}
                        </Button>
                      </div>
                    </div>
                    <div className="mt-2.5 grid grid-cols-2 gap-3 border-t border-[var(--th-card-border)] pt-2 sm:grid-cols-3 lg:grid-cols-5">
                      <RowMeta label={t("workers.meta.profile")}>#{row.userProfileId}</RowMeta>
                      <RowMeta label={t("workers.meta.employeeId")}>{row.providerEmployeeId ?? "—"}</RowMeta>
                      <RowMeta label={t("workers.meta.sentTo")}>
                        {/* Anything other than production is the provider's own environment name. */}
                        {row.providerEnvironmentName === "production" ? t("common.env.live") : row.providerEnvironmentName}
                      </RowMeta>
                      <RowMeta label={t("workers.meta.tries")}>{row.attemptCount}</RowMeta>
                      <RowMeta label={t("workers.meta.whatHappened")}>
                        {/* Server-produced error / gate text — shown verbatim. */}
                        <span title={row.error ?? row.gateReason ?? undefined}>{row.error ?? row.gateReason ?? "—"}</span>
                      </RowMeta>
                    </div>
                  </RowCard>
                )
              })
            )}
          </RowList>
          <TablePager
            totalCount={filteredSorted.length}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size)
              setPage(1)
            }}
          />
        </div>
      )}

      <MigrationDetailDialog
        detail={detail}
        onClose={() => setDetail(null)}
        onActionCompleted={() => {
          setDetail(null)
          void load()
        }}
      />
    </div>
  )
}
